import React, { useState, useEffect, useMemo } from 'react';
import { FieldProps } from 'formik';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import { withField } from './withFields';
import InputLabel from '@material-ui/core/InputLabel';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useStyles } from './dateSelectsStyles';
import { YearAndMonth } from 'common/interfaces';
import Grid, { GridSpacing } from '@material-ui/core/Grid';
import GridSubtitle from 'components/GridSubtitle/GridSubtitle';
import { getErrorMessageKey, yearAndMonthToSql } from 'helpers/helpers';
import Info from 'components/Tooltip/Info';
import FormHelperText from '@material-ui/core/FormHelperText';
import { useValueStyles } from './styles';

/**
 * Can be used with or without formik.
 * If fill as {DateSelects} you should provide:
 * name, customOnChange
 */

interface DateSelectProps {
  name: string;
  onChangeSubmit?: boolean;
  customOnChange?: (value: string) => void;
  label?: string;
  minDate?: YearAndMonth;
  maxDate?: YearAndMonth;
  yearsRange?: number; // how much years in select
  readOnly?: boolean;
  info?: React.ReactElement;
  infoUntil?: string;
  spacing?: GridSpacing;
  fixedMonth?: number;
  afterChange?: () => void;
  value?: any;
  readOnlyMonth?: boolean;
  readOnlyYear?: boolean;
  yearsValues?: number[]; // manually populate years select with these values
  disabled?: boolean;
}

type Props = DateSelectProps & Partial<FieldProps>;

const DateSelects: React.FC<Props> = ({
  field,
  form,
  meta,
  label,
  onChangeSubmit,
  minDate,
  maxDate,
  readOnly = false,
  info,
  spacing,
  fixedMonth,
  afterChange,
  value,
  name,
  customOnChange,
  readOnlyMonth,
  readOnlyYear,
  yearsValues,
  disabled,
  ...restProps
}) => {
  const classes = useStyles();
  const valueClasses = useValueStyles();
  const {
    i18n: { language },
    t,
  } = useTranslation();
  name = field?.name || name;
  value = field?.value || value;
  //afterChange = afterChange || (form && form.submitForm);

  const { year: actualYear = 2020 } = DateTime.local().toObject();
  const [innerMonth, setInnerMonth] = useState<string | number>('');
  const [innerYear, setInnerYear] = useState<string | number>('');
  const [limitError, setLimitError] = useState<string | null>(null);
  const [change, setChange] = useState(false);
  const [shouldSubmit, setShouldSubmit] = useState(false);
  const [manualChange, setManualChange] = useState(false);
  const realReadOnlyMonth = readOnly || readOnlyMonth;
  const realReadOnlyYear = readOnly || readOnlyYear;

  useEffect(() => {
    if (shouldSubmit && manualChange && !limitError) {
      //afterChange && afterChange();
      form && form.submitForm();
      setShouldSubmit(false);
      setManualChange(false);
    }
  }, [afterChange, limitError, shouldSubmit, manualChange, form]);

  useEffect(() => {
    if (change && manualChange && !limitError) {
      afterChange && afterChange();
      setChange(false);
      setManualChange(false);
    }
  }, [afterChange, limitError, change, manualChange]);

  const errorMessage = useMemo(
    () => (meta?.error ? t(getErrorMessageKey(meta?.error, false)) : ''),
    [meta, t],
  );

  let { yearsRange = 100 } = restProps;

  useEffect(() => {
    if (value) {
      // parse value from formik into own state
      const parsed = DateTime.fromSQL(value).toObject();
      const { month, year } = parsed;
      month && setInnerMonth(month);
      year && setInnerYear(year);
    }
  }, [value]);

  const monthsId = useMemo(() => `${name}_months`, [name]);
  const yearsId = useMemo(() => `${name}_years`, [name]);

  // start generate years from
  const startYear = useMemo(() => minDate?.years || actualYear - yearsRange / 2, [
    actualYear,
    minDate,
    yearsRange,
  ]);

  if (maxDate) {
    yearsRange = maxDate.years - (minDate?.years || actualYear) + 1;
  }

  useEffect(() => {
    // check valid dates
    if (!innerMonth || !innerYear) return;
    if (minDate && minDate.months > innerMonth && minDate.years >= innerYear) {
      const limitError = `${t('formfieldError_dateSelect_limitMinDate')} ${DateTime.fromObject({
        month: minDate.months,
        year: minDate.years,
      })
        .setLocale(language)
        .toFormat('LLLL yyyy')}.`;

      setLimitError(limitError);
      return;
    }

    if (maxDate && maxDate.months < innerMonth && maxDate.years <= innerYear) {
      const limitError = `${t('formfieldError_dateSelect_limitMaxDate')} ${DateTime.fromObject({
        month: maxDate.months,
        year: maxDate.years,
      })
        .setLocale(language)
        .toFormat('LLLL yyyy')}.`;

      setLimitError(limitError);
      return;
    }

    setLimitError(null);
  }, [innerMonth, innerYear, setLimitError, language, maxDate, minDate, t]);

  const handleMonthChange = React.useCallback(
    (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      const { value } = e.target;
      setInnerMonth(value as string);
      setManualChange(true);
    },
    [],
  );

  const handleYearChange = React.useCallback(
    (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      const { value } = e.target;
      setInnerYear(value as string);
      setManualChange(true);
    },
    [],
  );

  // set component state to formik
  useEffect(() => {
    if (innerMonth && innerYear) {
      const newValue = yearAndMonthToSql(`${innerYear}`, innerMonth);

      if (newValue !== value && manualChange) {
        const value = yearAndMonthToSql(`${innerYear}`, innerMonth);
        form?.setFieldValue(name, value, false);
        customOnChange && customOnChange(value);

        if (afterChange) {
          setChange(true);
        }

        if (onChangeSubmit) {
          setShouldSubmit(true);
        }
      }
    }
  }, [form, innerMonth, innerYear, manualChange, name, onChangeSubmit, value, customOnChange, afterChange]);

  const months = useMemo(
    () =>
      [...Array(12).keys()].map((index: number) => {
        const monthIndex = index + 1;
        const date = DateTime.fromObject({ month: monthIndex })
          .setLocale(language)
          .toFormat('LLLL');

        const isMonthDisabled =
          (minDate?.years === innerYear && minDate?.months > monthIndex) ||
          (maxDate?.years === innerYear && maxDate.months < monthIndex);

        return (
          <MenuItem value={monthIndex} key={index} disabled={isMonthDisabled}>
            {date}
          </MenuItem>
        );
      }),
    [innerYear, language, maxDate, minDate],
  );

  const years = useMemo(
    () =>
      (yearsValues || [...Array(yearsRange).keys()]).map((index: number) => {
        const value = yearsValues ? index : startYear + index;
        return (
          <MenuItem value={value} key={index}>
            {value}
          </MenuItem>
        );
      }),
    [startYear, yearsRange, yearsValues],
  );

  return (
    <>
      <Grid container item spacing={spacing}>
        {label && (
          <GridSubtitle>
            {label}
            {info && <Info heading content={info} />}
          </GridSubtitle>
        )}

        <Grid item xs={7}>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel htmlFor={monthsId} className={classes.label}>
              {t('general_month')}
            </InputLabel>
            <Select
              className={classes.select}
              variant="outlined"
              onChange={handleMonthChange}
              value={innerMonth}
              input={<OutlinedInput labelWidth={50} name={monthsId} id={monthsId} />}
              error={!realReadOnlyMonth && !!limitError}
              readOnly={realReadOnlyMonth}
              classes={{ root: realReadOnlyMonth ? valueClasses.readOnly : '' }}
              disabled={disabled}
            >
              {months}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={5}>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel htmlFor={yearsId} className={classes.label}>
              {t('general_year')}
            </InputLabel>
            <Select
              className={classes.select}
              variant="outlined"
              onChange={handleYearChange}
              value={innerYear}
              error={!realReadOnlyYear && !!limitError}
              input={<OutlinedInput labelWidth={50} name={yearsId} id={yearsId} />}
              readOnly={realReadOnlyYear}
              classes={{ root: realReadOnlyYear ? valueClasses.readOnly : '' }}
              disabled={disabled}
              //tooltipContent={}
            >
              {years}
            </Select>
          </FormControl>
        </Grid>
        {!readOnly && (limitError || errorMessage) && (
          <FormHelperText className={classes.error}>{limitError || errorMessage}</FormHelperText>
        )}
      </Grid>
    </>
  );
};

export { DateSelects };
export default withField(DateSelects);
